/**
 * Signed public records — the enforcing implementation of the namespace rule.
 *
 * README's "The namespace rule" says every public node must be either *signed
 * and verified on read against a pub allowed to have written it*, or an
 * *untrusted hint*. That rule was documented but not implemented: each feature
 * hand-rolled its own `SEA.verify` and decided for itself what to bind. Ten of
 * them existed, no two agreeing, and the differences were exactly the bugs —
 * `connections/<id>` verified the signature and the signer's membership of the
 * pair it named, but never that the pair was the one whose soul it sat on, so
 * any peer could compute a victim's connection id and park a validly-signed
 * record of its OWN pair there.
 *
 * This module is the one implementation. A collection declares who may write
 * it; reading is then a single call that fails closed. There is nothing left for
 * a feature to get wrong, because there is nothing left for a feature to decide.
 *
 * ## What a stored record is bound to
 *
 * The signature covers a header, and the header names where the record lives:
 *
 * - `ns`   — the collection. A record cannot be lifted between collections.
 * - `soul` — the Gun key. A record cannot be lifted between souls, which is the
 *            check the connection bug was missing. Compared against the key the
 *            body was actually READ FROM, never against a field beside it: a
 *            self-consistent blob proves nothing about its location.
 * - `by`   — the signer, checked against the collection's writer set.
 * - `seq`  — a per-soul counter, so a superseded state cannot be re-put over a
 *            newer one. The high-water mark is PERSISTED: held only in memory it
 *            dies with the tab, and an attacker rolling a record back merely has
 *            to wait for a reload (see README §"Replay").
 * - `at`   — when it was signed. Carried for display; it decides nothing, because
 *            a clock is not evidence.
 * - `body` — the payload, as the exact JSON string that was signed. Re-parsed
 *            here, never re-serialized, so no round-trip can change the bytes a
 *            signature covers.
 *
 * ## What it deliberately does not do
 *
 * **Confidentiality.** The body is plaintext. Records only their parties may
 * READ belong in a sealed envelope (`sealedRecordStore`) instead. This is for
 * records that must be world-readable — a moderation queue, a connection
 * request, an inbox notice — but must not be world-*writable* in effect.
 *
 * **Availability.** Any peer can `put(null)` on a public soul. A tombstone is
 * therefore not a deletion signal: {@link readSigned} reports `absent`, and
 * subscribers keep the last good copy. Real deletion is a signed record whose
 * body says so.
 */

import SEA from './sea';

import { publicRoot, onSessionReset, sessionPair, sessionPub } from './gunClient';
import { stable, sha256Hex } from './canonical';
import { GUN_STORAGE_PREFIX } from './gunConfig';
import { gunOn, gunOnce, gunOnceDetailed, gunPut, subscribeToGunNode, type GunUnsub } from './gunWrite';

import { createPersistedMarkStore } from './persistedMarkStore';

type GunNode = any;

/** Envelope version. Bumping it invalidates every stored record; don't. */
const ENVELOPE_VERSION = 1 as const;

/** What actually sits on the Gun node. */
export type SignedEnvelope = {
  v: typeof ENVELOPE_VERSION;
  /** Collection name — binds the record to its namespace. */
  ns: string;
  /** Gun key the record must live under — binds it to its soul. */
  soul: string;
  /** Signer pub. */
  by: string;
  /** Per-soul monotonic counter; rejects rollback replay. */
  seq: number;
  /** ISO timestamp, informational. */
  at: string;
  /** Payload as the exact JSON string that was signed. */
  body: string;
  /** SEA signature by `by` over {@link headerPayload}. */
  sig: string;
};

/**
 * Who may have written a record.
 *
 * Returning `null` means "cannot tell right now" — a cold engagement read, an
 * offline relay — and is NOT the same as "nobody". The record is then reported
 * readable but unverified rather than rejected, so a slow read never looks like
 * an attack. Returning `[]` means "nobody may have written this" and rejects.
 */
export type WriterResolver<T> = (
  soul: string,
  body: T,
) => string[] | null | Promise<string[] | null>;

/**
 * How a record may change over its life.
 *
 * - `sequenced` — successive states, each with a higher `seq`. A lower seq is a
 *   rollback replay; the same seq with different content is a fork. Connections,
 *   dispute cases, verification requests.
 * - `immutable` — written once and never again. Any later body that differs is
 *   rejected outright, whatever its seq. Audit events, chat messages, abuse
 *   reports: records whose whole value is that they cannot be revised.
 */
export type Mutability = 'sequenced' | 'immutable';

export type SignedCollectionSpec<T> = {
  /** Collection name. Goes inside the signature; must be stable forever. */
  ns: string;
  /** Read node for a soul. */
  node: (soul: string) => GunNode;
  /** Write node, when it differs from the read node. Defaults to `node`. */
  writeNode?: (soul: string) => GunNode;
  /** Who may have written this record. See {@link WriterResolver}. */
  writers: WriterResolver<T>;
  /** Default `sequenced`. See {@link Mutability}. */
  mutability?: Mutability;
  /**
   * Shape check on the decoded body, run BEFORE the writer resolver — which is
   * usually the one thing here that touches the network, and must not be handed
   * a body that is not even the right shape.
   */
  validate?: (body: unknown) => body is T;
  /**
   * Read a record written before this collection used envelopes.
   *
   * Adopting the envelope changes the wire format, and a collection with live
   * data cannot simply stop understanding it — every existing connection,
   * dispute or grant would vanish from its owner's account. So migration is
   * part of the contract rather than a branch each feature bolts on: supply the
   * OLD verifier here, return `null` for anything it cannot vouch for, and the
   * store falls back to it whenever a body is not an envelope.
   *
   * Legacy records are reported `verified: true` with `reason: 'legacy'`, so a
   * caller can tell them apart — and are never WRITTEN. Every write is an
   * envelope, so a record converts the first time it changes. Delete this
   * (and the old verifier with it) once nothing pre-envelope is left.
   */
  legacy?: (raw: unknown, soul: string) => Promise<T | null>;
};

export type SignedReadReason =
  /** Nothing there, or a tombstone. Never treated as a deletion. */
  | 'absent'
  /** Not an envelope at all — missing fields, wrong types, unparseable body. */
  | 'malformed'
  /** Envelope from another collection. */
  | 'wrong_namespace'
  /** Envelope signed for another soul: a cross-node replay. */
  | 'wrong_soul'
  | 'unsigned'
  | 'bad_signature'
  /** Signature verified, but over different header bytes than the ones stored. */
  | 'payload_mismatch'
  /** Body failed the collection's shape check. */
  | 'invalid_body'
  /** Signed by a pub that is not allowed to write this record. */
  | 'writer_not_allowed'
  /** Could not determine the writer set — unverified, not rejected. */
  | 'writers_unresolved'
  /** An older state re-sent over a newer one. */
  | 'stale_replay'
  /** Same seq, different content: two writers forked the record. */
  | 'chain_fork'
  /** An `immutable` record came back different from the one already held. */
  | 'immutable_changed'
  /** Verified by the collection's pre-envelope verifier. See `spec.legacy`. */
  | 'legacy';

/** One record in an indexed subscription. */
export type SignedIndexedEntry<T> = { soul: string; read: SignedRead<T> };

/**
 * An indexed read. Same shape as the sealed half's {@link SealedIndexedRead} —
 * the two collection APIs are twins and a caller should not have to remember
 * which one it is holding.
 */
export type SignedIndexedRead<T> = {
  /** Members, by soul. Only records that decoded AND passed `belongsTo`. */
  records: SignedIndexedEntry<T>[];
  /**
   * How many souls the index has announced, bodies resolved or not.
   *
   * An index entry reaches a client long before the body it points at, and
   * during that window a list is empty despite the account plainly having
   * records. Without this the only way out of the window is a blind timer,
   * which renders "you have none" over real data.
   */
  indexed: number;
  /** True once every announced soul has been decided — kept or rejected. */
  complete: boolean;
};

export type SignedRead<T> = {
  record: T | null;
  /** True only when the signature verified against a confirmed allowed writer. */
  verified: boolean;
  by?: string;
  seq?: number;
  at?: string;
  reason?: SignedReadReason;
};

/* ------------------------------------------------------------------ *
 * Canonical bytes
 * ------------------------------------------------------------------ */

/**
 * The exact bytes signed. Every field that decides anything is in here — a
 * field beside the signature rather than inside it is a field any peer may
 * rewrite at will.
 */
function headerPayload(e: Omit<SignedEnvelope, 'sig'>): string {
  return stable({ v: e.v, ns: e.ns, soul: e.soul, by: e.by, seq: e.seq, at: e.at, body: e.body });
}

/** Content fingerprint for fork detection at equal seq. */
const bodyHash = sha256Hex;

/* ------------------------------------------------------------------ *
 * Session identity
 * ------------------------------------------------------------------ */

/** The current SEA pair / pub — the layer's single accessors. */
const myPair = sessionPair;
const myPub = sessionPub;

/* ------------------------------------------------------------------ *
 * Replay memory
 * ------------------------------------------------------------------ */

/**
 * Highest verified state seen per `ns/soul`, persisted per account. See
 * {@link createPersistedMarkStore} for the invariants (persistence, per-account
 * keying, memoized load, forward-only merge).
 */
const marks = createPersistedMarkStore(`${GUN_STORAGE_PREFIX}_signed_marks_`);

const markKey = (ns: string, soul: string) => `${ns}/${soul}`;

/**
 * Highest `seq` seen in a *validly signed* envelope, whether or not its signer
 * could be confirmed as an allowed writer.
 *
 * Split from {@link marks} because the two answer different questions, and
 * conflating them broke one of them either way:
 *
 * - {@link marks} decides what to REJECT, so it must only advance on a fully
 *   verified record — otherwise a stranger with any valid keypair could sign a
 *   high-seq record and permanently block the genuine one.
 * - This decides what seq to ALLOCATE on the next write. Held to the same
 *   standard, a record that read as `writers_unresolved` advanced nothing, so
 *   our next write reused a seq the other party had already passed — and every
 *   reader, them included, rejected it as `stale_replay`. The write reported
 *   success and the update was simply lost.
 *
 * Not persisted: it only has to beat what this session has actually seen, and
 * {@link marks} (which is persisted) is its floor on a cold start.
 */
const observedSeq = new Map<string, number>();

/** Note a validly signed seq for write-side allocation only. See {@link observedSeq}. */
function rememberObserved(key: string, seq: number): void {
  if (typeof seq !== 'number' || !Number.isFinite(seq)) return;
  if (seq > (observedSeq.get(key) ?? 0)) observedSeq.set(key, seq);
}

/** Load this account's marks. Safe to call on every read. */
export function loadSignedRecordMarks(pub: string): Promise<void> {
  return marks.load(pub);
}

/** Forget every mark (logout, account switch, tests). */
export function resetSignedRecordMarks(): void {
  marks.reset();
  // Per-account state too, even though it is never persisted.
  observedSeq.clear();
}

// onSessionReset, not a logout hook: an app can authenticate a different account
// over a live session without ever calling gunLogout (README §15).
try {
  onSessionReset(resetSignedRecordMarks);
} catch {
  /* non-browser test environments may lack the client */
}

/* ------------------------------------------------------------------ *
 * Verification
 * ------------------------------------------------------------------ */

/**
 * Is this body a signed envelope at all?
 *
 * Exported for the collections still carrying records written before this
 * module existed: they branch on it to pick the new verifier or their legacy
 * one. Nothing else should need it — reading a record is {@link verifySignedRecord}.
 */
export function isSignedEnvelope(raw: unknown): raw is SignedEnvelope {
  return isEnvelopeShaped(raw);
}

function isEnvelopeShaped(raw: any): raw is SignedEnvelope {
  return (
    !!raw &&
    typeof raw === 'object' &&
    raw.v === ENVELOPE_VERSION &&
    typeof raw.ns === 'string' &&
    typeof raw.soul === 'string' &&
    typeof raw.by === 'string' &&
    typeof raw.at === 'string' &&
    typeof raw.body === 'string' &&
    typeof raw.sig === 'string' &&
    typeof raw.seq === 'number' &&
    Number.isFinite(raw.seq) &&
    !!raw.ns &&
    !!raw.soul &&
    !!raw.by &&
    !!raw.sig
  );
}

/**
 * Recover what a signature actually covered.
 *
 * `SEA.verify` returns the signed value *parsed*, so signing a JSON string
 * hands back an object (README §14). Comparing that against the raw string
 * never matches, and rejects every genuine signature as a forgery — a mistake
 * this codebase has made more than once. Normalise before comparing.
 */
async function recoverHeader(sig: string, by: string): Promise<string | null> {
  try {
    const opened = await SEA.verify(sig, by);
    if (opened == null) return null;
    return typeof opened === 'string' ? opened : stable(opened);
  } catch {
    return null;
  }
}

/**
 * Verify a raw body read from `soul`, against the collection's rules.
 *
 * Every rejection is a distinct reason, because "it did not verify" is not
 * actionable and the reasons say very different things: `writers_unresolved` is
 * a slow read, `stale_replay` is an attack.
 */
export async function verifySignedRecord<T>(
  spec: SignedCollectionSpec<T>,
  soul: string,
  raw: unknown,
): Promise<SignedRead<T>> {
  // A `null` body is a tombstone anyone can write, including a peer that cannot
  // read the record. Never a deletion signal — see the file header.
  if (raw === null || raw === undefined) return { record: null, verified: false, reason: 'absent' };
  if (!isEnvelopeShaped(raw)) {
    // Pre-envelope record, if this collection still has any. Its own verifier
    // decides; anything it will not vouch for is malformed like any other
    // non-envelope body.
    if (spec.legacy) {
      const record = await spec.legacy(raw, soul).catch(() => null);
      if (record) return { record, verified: true, reason: 'legacy' };
    }
    return { record: null, verified: false, reason: 'malformed' };
  }

  const e = raw;
  if (e.ns !== spec.ns) return { record: null, verified: false, reason: 'wrong_namespace' };
  // THE anchor: what the envelope claims, against where it was actually found.
  if (e.soul !== soul) return { record: null, verified: false, reason: 'wrong_soul' };

  const recovered = await recoverHeader(e.sig, e.by);
  if (recovered === null) return { record: null, verified: false, reason: 'bad_signature' };
  if (recovered !== headerPayload(e)) {
    return { record: null, verified: false, reason: 'payload_mismatch' };
  }

  let body: unknown;
  try {
    body = JSON.parse(e.body);
  } catch {
    return { record: null, verified: false, reason: 'malformed' };
  }
  if (spec.validate && !spec.validate(body)) {
    return { record: null, verified: false, reason: 'invalid_body' };
  }
  const record = body as T;
  const meta = { by: e.by, seq: e.seq, at: e.at };

  let allowed: string[] | null;
  try {
    allowed = await spec.writers(soul, record);
  } catch {
    allowed = null;
  }
  if (allowed && !allowed.includes(e.by)) {
    return { record: null, verified: false, reason: 'writer_not_allowed', ...meta };
  }

  const key = markKey(spec.ns, soul);

  // Replay is evaluated LAST, and only for a confirmed writer. Running it
  // earlier lets a stranger's envelope influence how a real writer's record
  // reads — it is rejected either way, but the reason would name the chain
  // instead of the stranger, and a non-party has no business moving the mark.
  if (!allowed) {
    // The gate mark must NOT move (a stranger's valid keypair would block the
    // genuine record) but the write-side mark must, or our next write reuses a
    // seq the real writer has already passed. See {@link observedSeq}.
    //
    // Bounded, because "unresolved" includes strangers: anyone can sign a
    // well-formed envelope for this soul with an absurd seq and inflate every
    // future allocation. A genuine writer only ever runs ahead by the states
    // written while this device was not looking, so a cap far above any real
    // burst keeps the legitimate flow and strips the stranger's leverage.
    // Capped against the GATE mark, not against observedSeq itself — relative
    // to itself, repeated stranger envelopes ratchet it up +100 at a time
    // without bound.
    const pubForMarks = myPub();
    if (pubForMarks) await loadSignedRecordMarks(pubForMarks);
    if (e.seq <= (marks.get(key)?.seq ?? 0) + 100) rememberObserved(key, e.seq);
    return { record, verified: false, reason: 'writers_unresolved', ...meta };
  }

  const pub = myPub();
  if (pub) await loadSignedRecordMarks(pub);
  // Hash BEFORE reading the mark: with an await between the read and the set,
  // two concurrent verifications could interleave so the lower seq wrote its
  // mark last, regressing the high-water mark and reopening the replay window.
  const hash = await bodyHash(e.body);
  const known = marks.get(key);

  if (known) {
    if (spec.mutability === 'immutable') {
      // Nothing about an immutable record may change — not its body, not its
      // seq. Re-reading the identical record is normal and stays fine.
      if (hash !== known.hash) {
        return { record: null, verified: false, reason: 'immutable_changed', ...meta };
      }
    } else {
      if (e.seq < known.seq) return { record: null, verified: false, reason: 'stale_replay', ...meta };
      if (e.seq === known.seq && hash !== known.hash) {
        return { record: null, verified: false, reason: 'chain_fork', ...meta };
      }
    }
  }
  marks.advance(key, { seq: e.seq, hash });

  return { record, verified: true, ...meta };
}

/* ------------------------------------------------------------------ *
 * Collection handle
 * ------------------------------------------------------------------ */

export type PutOptions = {
  /** Names the write in log output. */
  label?: string;
  /**
   * Skip the seed read for a soul this client has never seen.
   *
   * That read exists so a second device does not allocate a `seq` another
   * device has already used. It is pure latency for an id minted a moment ago —
   * and not cheap latency: a soul the graph has never heard of costs the full
   * absent-grace window, so every record creation paid ~1.5s for the answer
   * "no, of course not". Set this only where the soul is freshly generated.
   */
  isNew?: boolean;
};

export type SignedCollection<T> = {
  ns: string;
  /**
   * Sign and write a record. Allocates the next `seq` from the highest state
   * this client has seen, so two devices editing the same record produce
   * different seqs rather than silently forking at the same one.
   *
   * Returns an error string rather than throwing: these calls sit inside
   * lifecycle operations that already report `{ ok: false }`, and a sealing
   * failure must never reach the user as an unhandled rejection.
   *
   * `'seq_unknown'` means the graph did not answer the seed read, so no seq can
   * be allocated safely — a retryable failure, not a rejection. Nothing was
   * written. See the fail-closed note in the implementation.
   */
  write(soul: string, body: T, opts?: PutOptions): Promise<string | null>;
  /** One-shot verified read. `record: null` with a reason, never a bare null. */
  read(soul: string): Promise<SignedRead<T>>;
  /** One-shot verified read, discarding provenance. Prefer {@link read}. */
  get(soul: string): Promise<T | null>;
  /** Live verified read of one record. Tombstones are ignored, not applied. */
  subscribe(soul: string, cb: (read: SignedRead<T>) => void): GunUnsub;
  /**
   * Live verified read of a whole collection, driven by an index node.
   *
   * The index is treated as an untrusted HINT throughout, which is the rule
   * README states and the one the connection list broke: an index entry under
   * your pub is something any peer can write, so membership is decided by the
   * verified BODY (`belongsTo`), never by the entry's presence.
   */
  subscribeIndexed(
    indexNode: GunNode,
    cb: (read: SignedIndexedRead<T>) => void,
    opts: {
      /**
       * REQUIRED, deliberately: a verified envelope of the right collection can
       * still be a genuine record between two OTHER parties, planted under this
       * index by anyone. Membership must come from the verified body. There is
       * no safe default — omitting this was exactly the planted-entry bug.
       */
      belongsTo: (record: T, soul: string) => boolean;
      batchMs?: number;
    },
  ): GunUnsub;
};

/**
 * Next seq to write for a soul: one past the highest state we have seen —
 * verified or merely validly signed (see {@link observedSeq}).
 */
function nextSeq(ns: string, soul: string): number {
  const key = markKey(ns, soul);
  return Math.max(marks.get(key)?.seq ?? 0, observedSeq.get(key) ?? 0) + 1;
}

/**
 * Declare a signed public collection.
 *
 * Everything the namespace rule requires is decided here, once, at the
 * declaration — not at each of the call sites that read it.
 */
export function defineSignedCollection<T>(spec: SignedCollectionSpec<T>): SignedCollection<T> {
  const readNode = spec.node;
  const writeNode = spec.writeNode ?? spec.node;

  const read = async (soul: string): Promise<SignedRead<T>> => {
    const raw = await gunOnce<any>(readNode(soul));
    return verifySignedRecord(spec, soul, raw);
  };

  /**
   * The seed read behind seq allocation, which needs absent-vs-unknown where
   * {@link read} does not.
   *
   * `gunOnce` reports both as `null`, and the difference decides whether the
   * record is new (seq 1 is right) or merely unanswered (seq 1 is a rollback of
   * whatever the other party is holding). Getting it wrong is silent: the write
   * succeeds locally and every reader drops it as `stale_replay`.
   */
  const seedMark = async (soul: string): Promise<'ok' | 'unknown'> => {
    const seed = await gunOnceDetailed<any>(readNode(soul));
    if (seed.status === 'unknown') return 'unknown';
    // Absent is a real answer — nothing to seed from, seq 1 is correct.
    if (seed.value == null) return 'ok';
    // Verifying is what moves the marks; the read itself is discarded.
    await verifySignedRecord(spec, soul, seed.value);
    return 'ok';
  };

  return {
    ns: spec.ns,

    async write(soul: string, body: T, opts?: PutOptions): Promise<string | null> {
      const pair = myPair();
      const pub = myPub();
      if (!pair || !pub) return 'no_session';
      if (!soul) return 'no_soul';

      // The seq must be allocated from persisted state, or a write after a
      // reload restarts at 1 and every reader sees it as a rollback.
      await loadSignedRecordMarks(pub);
      // Seed from the graph when this client has never seen the record: another
      // device may have advanced it, and writing seq 1 over their seq 4 is a
      // fork we would be creating ourselves.
      //
      // FAIL CLOSED when the graph does not answer — for a `sequenced`
      // collection. The seed read exists precisely to stop a rollback, so
      // treating "no answer" as "no record" defeats it exactly when it is
      // needed: a slow relay is also when the other party holds states this
      // client has not seen.
      //
      // NOT for `immutable`, where the seed read answers a different question
      // (does this record already exist?) and there is no rollback to prevent —
      // the seq is always 1 and any later differing body is rejected on read.
      // Refusing there would only stop an offline client from appending an
      // audit event, which is the one record kind that must never be lost.
      if (!opts?.isNew && !marks.has(markKey(spec.ns, soul))) {
        const seeded = await seedMark(soul);
        if (seeded === 'unknown' && spec.mutability !== 'immutable') return 'seq_unknown';
      }

      // An immutable record is written once. Enforced on the WRITE side too,
      // not only on read: a client that revises one produces a record every
      // other client is obliged to reject, so letting the write through would
      // only manufacture a record that exists here and nowhere else.
      if (spec.mutability === 'immutable' && marks.has(markKey(spec.ns, soul))) {
        return 'immutable_exists';
      }

      const serialized = JSON.stringify(body);
      if (typeof serialized !== 'string') return 'unserializable_body';

      const header: Omit<SignedEnvelope, 'sig'> = {
        v: ENVELOPE_VERSION,
        ns: spec.ns,
        soul,
        by: pub,
        seq: nextSeq(spec.ns, soul),
        at: new Date().toISOString(),
        body: serialized,
      };
      let sig: unknown;
      try {
        sig = await SEA.sign(headerPayload(header), pair);
      } catch {
        sig = null;
      }
      if (typeof sig !== 'string' || !sig) return 'signing_failed';

      const envelope: SignedEnvelope = { ...header, sig };
      // Remember our own write before it lands: the echo comes back through the
      // same verification path, and without the mark a second write in the same
      // session would reuse this seq and read as a fork of our own record.
      // Advance-only: a concurrent verification may have moved the mark past
      // this seq while the hash was computed, and it must not regress.
      marks.advance(markKey(spec.ns, soul), { seq: header.seq, hash: await bodyHash(serialized) });

      gunPut(writeNode(soul), envelope as any, { label: opts?.label ?? `${spec.ns} ${soul}` });
      return null;
    },

    read,

    get(soul: string): Promise<T | null> {
      return read(soul).then((r) => r.record);
    },

    subscribe(soul: string, cb: (read: SignedRead<T>) => void): GunUnsub {
      return gunOn(readNode(soul), (raw: any) => {
        // A tombstone is not a deletion (file header): keep the last good copy
        // rather than letting any peer blank the record with one put.
        if (raw === null || raw === undefined) return;
        void verifySignedRecord(spec, soul, raw).then(cb);
      });
    },

    subscribeIndexed(
      indexNode: GunNode,
      cb: (read: SignedIndexedRead<T>) => void,
      opts: { belongsTo: (record: T, soul: string) => boolean; batchMs?: number },
    ): GunUnsub {
      const bySoul = new Map<string, SignedRead<T>>();
      /** Souls the index has announced. Tracked apart from `bySoul` — see `indexed`. */
      const announced = new Set<string>();
      /**
       * Souls whose body has produced a verdict — member or not.
       *
       * `complete` used to compare `announced` against the MEMBER set, which
       * meant one indexed soul that never becomes a member held the read open
       * forever: a planted entry, a record belonging to someone else, or — the
       * way it actually surfaced — a record left unreadable by a wire-format
       * change. The list then showed "still loading" permanently.
       *
       * Deciding a soul is not the same as keeping it. This tracks the decision.
       */
      const resolved = new Set<string>();
      // One LIVE listener per body. A `.once()` here samples the record at the
      // instant its index entry arrives and never looks again, so a state the
      // other party writes later never appears — and on a cold read, where the
      // index arrives first, the body is dropped and never retried (README §4).
      const bodyUnsubs = new Map<string, GunUnsub>();

      const emit = () =>
        cb({
          records: [...bySoul.entries()].map(([soul, read]) => ({ soul, read })),
          indexed: announced.size,
          complete: announced.size === resolved.size,
        });

      const forget = (soul: string) => {
        bodyUnsubs.get(soul)?.();
        bodyUnsubs.delete(soul);
        announced.delete(soul);
        resolved.delete(soul);
        bySoul.delete(soul);
      };

      const subscription = subscribeToGunNode(indexNode, {
        onUpsert: (data: any, soul: string) => {
          if (!data || soul === '_') {
            forget(soul);
            emit();
            return;
          }
          if (bodyUnsubs.has(soul)) return;
          // Announce as soon as the index names it, so a consumer can keep
          // showing a loading state instead of "you have none".
          announced.add(soul);
          emit();
          bodyUnsubs.set(
            soul,
            gunOn(readNode(soul), (raw: any) => {
              if (raw === null || raw === undefined) return; // tombstone
              void verifySignedRecord(spec, soul, raw).then((r) => {
                if (!r.record) {
                  // A rejected read (stale_replay, bad_signature, …) is an
                  // attacker's put, not new information about the record. It
                  // must not evict the verified copy already held — otherwise
                  // any peer could blank a record from every subscriber's list
                  // by replaying an old envelope.
                  //
                  // Still RESOLVED: a rejection is a decision. Leaving it
                  // undecided is what held `complete` false forever.
                  resolved.add(soul);
                  emit();
                  return;
                }
                // The index is a hint. Membership is whatever the verified body
                // says, so a planted entry resolves to nothing. Default-DENY at
                // runtime for untyped callers, and for a rule that throws: Gun
                // delivers a node field by field, so a membership rule is
                // routinely handed a half-arrived body, and an escaping
                // TypeError would take down the whole subscription.
                let belongs = false;
                try {
                  belongs = typeof opts?.belongsTo === 'function'
                    ? opts.belongsTo(r.record, soul)
                    : false;
                } catch {
                  belongs = false;
                }
                resolved.add(soul);
                if (belongs) bySoul.set(soul, r);
                else bySoul.delete(soul);
                emit();
              });
            }),
          );
        },
        onDelete: (soul: string) => {
          forget(soul);
          emit();
        },
        batchMs: opts?.batchMs ?? 150,
      });

      return () => {
        subscription?.unsubscribe();
        bodyUnsubs.forEach((off) => off());
        bodyUnsubs.clear();
      };
    },
  };
}

/** Convenience: a collection stored at `<root>/<ns>/<soul>`. */
export function publicCollection<T>(
  spec: Omit<SignedCollectionSpec<T>, 'node'>,
): SignedCollection<T> {
  return defineSignedCollection<T>({
    ...spec,
    node: (soul) => publicRoot().get(spec.ns).get(soul),
  });
}
